<template lang="pug">
div
  UpsertResource(
    :forEditorModel="forEditorModel"
    :resource="resource"
    :disabled="isSaving"
    :isSaving="isSaving"
    :segmentConfiguration="segmentConfiguration"
    :enableMultilanguage="enableMultilanguage"
    :enableResourceGroups="enableResourceGroups"
    :showAuditTrail="showAuditTrail"
    :vuelidate="$v"
    @save="validateAndSave"
    @cancel="$emit('cancel')"
    )
  BriqMultilanguageWarningModal(
    v-if="hasMultilanguage && resource"
    v-show="showTranslationsWarningModal"
    :defaultModel="resource"
    :translationInfo="resource.TranslationInfo"
    :translatableProperties="translatableProperties"
    @close="showTranslationsWarningModal = false" 
    @onSubmit="save"
    )
</template>

<script>
import { required, minValue, minLength } from 'vuelidate/lib/validators'
import { validateProps } from '@/vue/util/propValidators'
import { clone, pipe, prop, append, concat, propSatisfies, lte, ifElse, length } from 'ramda'
import UpsertResource from './UpsertResource.vue'
import BriqMultilanguageWarningModal from '@v/components/BriqMultilanguageWarningModal.vue'
import UpsertResourceModel from './upsertResourceModel.js'
import validateSlug from '@/helpers/validateSlug.js'
import ResourceType from './ResourceType.js'
import { emptyTranslatableFieldsExist } from '@v/util/emptyTranslatableFields.js'
import completeTranslationInfo from '@v/util/completeTranslationInfo.js'
import ResourceLinkingToExternalResource from '@v/inventory/products/edit/models/ResourceLinkingToExternalResource.js'

export default {
  name: 'UpsertResourceValidator',
  components: { UpsertResource, BriqMultilanguageWarningModal },
  props: {
    forEditorModel: {
      type: Object,
      required: true,
      validator: validateProps([
        'Resource',
        'SegmentConfigurations',
        'FeedResources',
        'Resources',
        'ResourceTypes',
        'ExternalTypes',
        'Settings',
      ]),
    },
    businessUnitKey: { type: String, required: true },
    showAuditTrail: Boolean,
    enableMultilanguage: Boolean,
    enableResourceGroups: Boolean,
    segmentConfiguration: Object, // todo: refactor
    isSaving: Boolean,
  },
  data() {
    return {
      resource: new UpsertResourceModel(this.forEditorModel.Resource, this.businessUnitKey),
      showTranslationsWarningModal: false,
    }
  },
  validations() {
    const validations = {
      resource: {
        Description: { required },
        Slug: { required, validateSlug },
      },
    }
    switch (this.resource.ResourceType) {
      case ResourceType.SubResource:
        validations.resource.Priority = { required, minValue: minValue(0) }
        validations.resource.ParentResourceKey = { required }
        break
      case ResourceType.ResourceGroup:
        validations.resource.Capacity = this.resource.ShowOnScheduler
          ? { required, minValue: minValue(0) }
          : {}
        validations.resource.TeamSize = { required }
        break
      case ResourceType.Normal:
      default:
        validations.resource.Capacity = this.resource.ShowOnScheduler
          ? { required, minValue: minValue(1) }
          : {}
        validations.resource.TeamSize = { required }
        validations.resource.Priority = { required, minValue: minValue(0) }
        break
    }
    // if an integration provider is selected - which is set on ExternalType - maybe an external resource must be selected
    // but only if it's not a resource group because then they must be selected on the subresources
    // otherwise, check the requirements on ResourceLinkingToExternalResource
    if (this.resource.ExternalType && !this.resource.isResourceGroup()) {
      const externalTypeConfig = this.forEditorModel.ExternalTypes.find(
        et => et.ExternalType === this.resource.ExternalType,
      )
      if (externalTypeConfig) {
        // validations.resource.ExternalResources = { required, minLength: minLength(1) }
        switch (externalTypeConfig.FeatureFlags.ResourceLinkingToExternalResource) {
          case ResourceLinkingToExternalResource.Single:
            validations.resource.ExternalResources = { required, single: x => length(x) === 1 }
            break
          case ResourceLinkingToExternalResource.Multiple:
            validations.resource.ExternalResources = { required, minLength: minLength(1) }
            break
          case ResourceLinkingToExternalResource.NotAllowed:
          default:
            break
        }
      }
    } // todo: write unit tests for this
    return validations
  },
  computed: {
    hasMultilanguage() {
      return this.enableMultilanguage && !!this.resource.hasAvailableCultures()
    },
  },
  methods: {
    validateAndSave() {
      this.$v.$touch()
      if (this.$v.resource.$error) {
        this.$notifyError('Please fill out all required fields')
        return
      }
      const invalidFields = getInvalidFields(
        this.resource,
        this.forEditorModel.ResourceTypes.map(x => x.ResourceType),
      )
      if (invalidFields.length > 0) {
        this.$logError('invalid fields:', invalidFields)
        this.$notifyError('Please fill out all required fields')
        return
      }
      const translationsNotDone =
        this.hasMultilanguage &&
        emptyTranslatableFieldsExist(
          this.resource,
          this.resource.TranslationInfo,
          this.translatableProperties,
        )
      if (translationsNotDone) this.showTranslationsWarningModal = true
      else this.save()
    },
    save() {
      this.$emit('save', deleteRedundantKeys(this.resource, this.forEditorModel.ResourceTypes))
    },
  },
  beforeMount() {
    if (this.hasMultilanguage) {
      this.culture = this.availableCultures[0]
      completeTranslationInfo(this.resource)
    }
  },
}

// util

function deleteRedundantKeys(resource, resourceTypes) {
  const result = clone(resource)
  const allowedFields = resourceTypes.find(item => item.ResourceType === resource.ResourceType)
    .ActiveFields
  for (const property in resource) {
    if (!allowedFields.includes(property)) {
      delete result[property]
    }
  }
  result.ChildResources?.forEach(childResource => {
    delete childResource.TempKey
  })
  return result
}

// extra validations

function getResourceRequirements(resource, resourceTypes) {
  const makeFieldValidator = v => f => ({ field: f, validator: v(f) })
  const isTruthy = makeFieldValidator(propSatisfies(x => !!x))
  const propIsAtLeast = n => propSatisfies(pipe(parseInt, lte(n)))
  const isAtLeast = n => makeFieldValidator(propIsAtLeast(n))
  const hasValidResourceType = makeFieldValidator(propSatisfies(x => resourceTypes.includes(x)))(
    'ResourceType',
  )
  const generalRequirements = [hasValidResourceType, isTruthy('Description'), isTruthy('Slug')]
  const hasValidCapacity = {
    field: 'Capacity',
    validator: ifElse(
      prop('ShowOnScheduler'),
      propIsAtLeast(1)('Capacity'),
      propIsAtLeast(0)('Capacity'),
    ),
  }

  const getScheduledRequirements = resource => {
    switch (resource.ResourceType) {
      case ResourceType.SubResource:
        return concat(
          [isAtLeast(0)('Priority'), isTruthy('ParentResourceKey')],
          generalRequirements,
        )
      case ResourceType.ResourceGroup:
        return append(hasValidCapacity, generalRequirements)
      case ResourceType.Normal:
      default:
        return concat([hasValidCapacity, isAtLeast(0)('Priority')], generalRequirements)
    }
  }

  return resource.ShowOnScheduler
    ? getScheduledRequirements(resource)
    : concat([isAtLeast(0)('Priority')], generalRequirements)
}

function getInvalidFields(resource, resourceTypes) {
  const requirements = getResourceRequirements(resource, resourceTypes)
  return requirements.filter(v => !v.validator(resource)).map(prop('field'))
}
</script>
